import {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
  useEffect,
} from "react";
import html2pdf from "html2pdf.js";
import { ContratoServiciosProps } from "@/types";
import Empresa from "./Empresa";
import Profesional from "./Profesional";
import { EstilosPdfCss } from "../../../styles";
import { blobToBase64, defaultPdfOptions } from "../../../utils/pdfHelpers";

export interface ContratoPdfRef {
  // ahora acepta opcionalmente números para forzar el texto en el PDF
  generarPdfComoBase64: (
    numeroContrato?: string,
    numeroFactura?: string,
  ) => Promise<string | null>;
}

const ContratoServicios = forwardRef<ContratoPdfRef, ContratoServiciosProps>(
  (
    {
      selectedProfesiones,
      formData1,
      paymentMethod,
      isEmpresa,
      currentDate,
      firmaBase64,
      numeroContrato,
      numeroFactura,
      totalPagar,
    },
    ref,
  ) => {
    const contratoRef = useRef<HTMLDivElement>(null);
    const [numContrato, setNumContrato] = useState<string>(
      numeroContrato ?? "",
    );
    const [numFactura, setNumFactura] = useState<string>(numeroFactura ?? "");
    const [firmaUrl] = useState<string | null>(null);

    // Cuando subas la firma a S3:
    /* async function handleFirmaUpload(file: File) { 
    const res = await subirFirma(file); // tu función de subida 
    setFirmaUrl(res.url); // guardas la URL de S3 
  }
     */
    useEffect(() => {
      if (numeroContrato) setNumContrato(numeroContrato);
    }, [numeroContrato]);

    useEffect(() => {
      if (numeroFactura) setNumFactura(numeroFactura);
    }, [numeroFactura]);

    const generarPdfComoBase64 = async (
      numContratoParam?: string,
      numFacturaParam?: string,
    ): Promise<string | null> => {
      if (!contratoRef.current) return null;

      // determinar qué mostrar (prioriza los params pasados)
      const displayContrato = numContratoParam ?? numContrato ?? "Cargando...";
      const displayFactura = numFacturaParam ?? numFactura ?? "Cargando...";

      // actualizar estado (para consistencia)
      setNumContrato(displayContrato);
      setNumFactura(displayFactura);

      // también escribir directamente en el DOM para evitar esperar al re-render
      const root = contratoRef.current;
      const spanContrato = root.querySelector(
        "[data-num-contrato]",
      ) as HTMLElement | null;
      const spanFactura = root.querySelector(
        "[data-num-factura]",
      ) as HTMLElement | null;
      if (spanContrato) spanContrato.textContent = displayContrato;
      if (spanFactura) spanFactura.textContent = displayFactura;

      // asegurar que el navegador pinta el cambio antes de capturar
      await new Promise(requestAnimationFrame);

      try {
        const options = { ...defaultPdfOptions, filename: "contrato.pdf" };
        const pdfBlob = await html2pdf()
          .from(root)
          .set(options)
          .outputPdf("blob");
        return await blobToBase64(pdfBlob);
      } catch (error) {
        console.error("Error generando PDF:", error);
        return null;
      }
    };

    useImperativeHandle(ref, () => ({ generarPdfComoBase64 }));

    // 🎨 Renderizado del contrato en formato HTML (base del PDF)
    return (
      <div
        ref={contratoRef}
        className={`${EstilosPdfCss.listaPdf} p-0 m-0 md:p-4 md:m-2 bg-white text-[14.5px] text-zinc-800 my-0 overflow-auto`}
      >
        {/* HEADER */}
        <div className="pdf-section flex justify-between items-center mb-6">
          <img
            src="/img/logo/LogoLinkServs.png"
            alt="Logo"
            className="text-start w-24 h-20"
          />
        </div>

        {/* TÍTULO */}
        <div className="pdf-section">
          <h1 className="text-2xl font-bold text-center mb-4">
            CONTRATO DE PRESTACIÓN DE SERVICIOS
          </h1>
        </div>

        {/* DATOS */}
        <div className="pdf-section mb-3">
          <p>
            <strong>Contrato Nº:</strong>{" "}
            <span data-num-contrato>{numContrato}</span>
          </p>
          <p>
            <strong>Factura Nº relacionada:</strong>{" "}
            <span data-num-factura>{numFactura}</span>
          </p>
          <p>En Toledo, a {currentDate}</p>
        </div>

        {/* REUNIDOS */}
        <div className="pdf-section mb-6">
          <h2 className="font-semibold mb-1">REUNIDOS:</h2>
          <p>
            <strong>De una parte:</strong> La Empresa, cuyos datos son los
            siguientes:
          </p>
          <Empresa />

          {/* Introducción del profesional en el contrato */}
          <p className="my-4">
            <strong>Y de otra parte:</strong> El Profesional, cuyos datos son
            los siguientes:
          </p>

          <Profesional
            selectedProfesiones={selectedProfesiones}
            formData1={formData1}
            isEmpresa={isEmpresa}
          />
        </div>

        {/* INTRODUCCIÓN LEGAL */}
        <div className="pdf-section mb-6">
          <p>
            <em>
              Ambas partes, en adelante denominadas conjuntamente como "Las
              Partes", reconocen tener la capacidad legal necesaria para
              suscribir el presente contrato, y a tal efecto:
            </em>
          </p>
        </div>

        {/* EXPONEN */}
        <div className="pdf-section mb-6">
          <h2 className="font-semibold mb-2">EXPONEN</h2>
          <div className="ml-4 space-y-2">
            <p>
              <em>
                Que La Empresa es titular de una página web en la que promociona
                profesionales de diferentes sectores mediante la publicación de
                sus datos y servicios ofrecidos.
              </em>
            </p>
            <p>
              <em>
                Que El Profesional desea contratar los servicios de visibilidad
                ofrecidos por La Empresa con el fin de promocionar su actividad
                profesional en dicha plataforma.
              </em>
            </p>
            <p>
              <em>
                Que ambas partes están interesadas en formalizar un contrato que
                regule sus derechos y obligaciones.
              </em>
            </p>
          </div>
        </div>

        <div className="pdf-section">
          <p>Por lo tanto, acuerdan lo siguiente:</p>
        </div>

        {/* CLÁUSULAS */}
        <div className="pdf-section">
          <h2 className="font-semibold mb-2">CLÁUSULAS:</h2>
        </div>

        {/* PRIMERA */}
        <div className="pdf-section">
          <h3 className="font-semibold mb-2">Primera. Objeto del contrato:</h3>
          <p>
            El objeto del presente contrato es la prestación por parte de La
            Empresa del servicio de publicación de los datos profesionales de El
            Profesional en su página web, con el objetivo de dar visibilidad a
            los servicios que este último presta.
          </p>
        </div>

        {/* SEGUNDA */}
        <div className="pdf-section">
          <h3 className="font-semibold mb-2">
            Segunda. Obligaciones de las partes:
          </h3>

          <p className="ml-4 mb-2 font-semibold">Obligaciones de La Empresa:</p>
          <div className="ml-8 mb-4 space-y-2">
            <p>
              Publicar los datos e información profesional facilitada por El
              Profesional en la sección correspondiente del sitio web.
            </p>
            <p>
              Mantener la disponibilidad de la web salvo causas técnicas o
              fuerza mayor.
            </p>
          </div>

          <p className="ml-4 mb-2 font-semibold">
            Obligaciones de El Profesional:
          </p>
          <div className="ml-8 space-y-2">
            <p>Proporcionar información veraz y completa.</p>
            <p>Mantener su perfil actualizado.</p>
            <p>Abonar el importe acordado.</p>
          </div>
        </div>

        {/* TERCERA */}

        <div className="pdf-section">
          <h3 className="font-semibold mb-2">
            Tercera. Duración del contrato:
          </h3>
          <p>
            Este contrato tendrá una duración <strong>{paymentMethod}</strong>,
            comenzando desde la fecha de su formalización y finalizando una vez
            transcurrido dicho periodo. La duración del presente contrato no
            será objeto de renovación automática. Una vez finalizado el periodo
            contratado, cualquier continuidad de la relación requerirá la
            formalización de un nuevo contrato y el correspondiente pago por el
            nuevo periodo.
          </p>
        </div>

        {/* CUARTA */}

        <div className="pdf-section">
          <h3 className="font-semibold mb-2">
            Cuarta. Precio y forma de pago:
          </h3>
          <p>
            El Profesional abonará{" "}
            <strong>{totalPagar?.toFixed(2)} euros</strong> IVA incluido,
            mediante el método de pago <strong>{paymentMethod}</strong>,
            correspondiente exclusivamente al periodo de duración establecido en
            la cláusula anterior.
          </p>
        </div>

        {/* QUINTA */}
        <div className="pdf-section">
          <h3 className="font-semibold mb-2">
            Quinta. No devolución del importe abonado:
          </h3>
          <p>
            El Profesional declara conocer y aceptar que, una vez realizado el
            pago y activados los servicios contratados, no procederá devolución
            alguna del importe abonado, con independencia del tiempo restante de
            servicio, del uso efectivo de la plataforma o de cualquier
            circunstancia personal o profesional del contratante.
          </p>
          <p className="mt-2">
            Esta condición se fundamenta en que el servicio contratado consiste
            en la puesta a disposición inmediata de medios digitales, acciones
            de visibilidad y recursos técnicos que se activan desde el momento
            del pago, lo cual implica la plena ejecución del servicio conforme
            al artículo 103.a de la Ley General para la Defensa de los
            Consumidores y Usuarios.
          </p>
        </div>

        {/* SEXTA */}
        <div className="pdf-section">
          <h3 className="font-semibold mb-2">Sexta. Resolución anticipada:</h3>
          <p>
            Cualquiera de las partes podrá resolver este contrato en caso de
            incumplimiento grave.
          </p>
        </div>

        {/* SÉPTIMA */}
        <div className="pdf-section">
          <h3 className="font-semibold mb-2">
            Séptima. Propiedad intelectual:
          </h3>
          <p>
            El Profesional autoriza a La Empresa a usar los materiales
            proporcionados para su promoción.
          </p>
        </div>

        {/* OCTAVA */}
        <div className="pdf-section">
          <h3 className="font-semibold mb-2">Octava. Confidencialidad:</h3>
          <p>
            Ambas partes se comprometen a mantener la confidencialidad de la
            información compartida.
          </p>
        </div>

        {/* NOVENA */}
        <div className="pdf-section">
          <h3 className="font-semibold mb-2">
            Novena. Protección de datos y consentimiento:
          </h3>
          <p>
            De conformidad con lo dispuesto en el Reglamento (UE) 2016/679
            (RGPD) y la Ley Orgánica 3/2018 de Protección de Datos Personales y
            garantía de los derechos digitales, El Profesional declara haber
            sido informado y aceptar expresamente el tratamiento de los datos
            personales facilitados, los cuales serán utilizados exclusivamente
            para la correcta ejecución del presente contrato y para la
            publicación de su perfil profesional en la plataforma de La Empresa.
          </p>

          <p className="mt-2">
            El Profesional autoriza expresamente la publicación de los datos
            profesionales, información de contacto y descripción de servicios
            facilitados, eximiendo a La Empresa de cualquier responsabilidad
            derivada de la veracidad de los mismos.
          </p>
        </div>
        {/* DÉCIMA */}
        <div className="pdf-section">
          <h3 className="font-semibold mb-2">
            Décima. Consentimiento de cesión de derechos de imagen:
          </h3>

          <p>
            El Profesional autoriza expresamente a{" "}
            <strong>CUADRANEX, S.L.</strong> a captar, reproducir, utilizar y
            publicar su imagen, voz y/o apariencia física mediante fotografías,
            grabaciones audiovisuales y cualquier otro soporte multimedia
            realizado en el entorno profesional o durante actividades
            relacionadas con la actividad empresarial.
          </p>

          <p className="mt-3">
            La presente autorización incluye la posibilidad de difusión y
            publicación del material audiovisual en los siguientes medios:
          </p>

          <div className="ml-6 mt-2 space-y-1">
            <p>
              • Redes sociales corporativas (Instagram, TikTok, Facebook,
              LinkedIn, X u otras similares).
            </p>
            <p>
              • Página web corporativa y plataformas digitales de la empresa.
            </p>
            <p>• Material publicitario, promocional y comercial.</p>
            <p>
              • Presentaciones corporativas, campañas de marketing y contenido
              digital o impreso relacionado con la actividad de la empresa.
            </p>
          </div>

          <p className="mt-3">
            Asimismo, El Profesional autoriza el uso de fotografías y vídeos de
            instalaciones, oficinas, vehículos, espacios de trabajo o
            actividades desarrolladas por la empresa en los que pudiera aparecer
            de forma identificable.
          </p>

          <p className="mt-3">
            El Profesional acepta que las imágenes, vídeos o grabaciones puedan
            ser editados, adaptados o modificados con fines promocionales,
            informativos o de comunicación corporativa, incluyendo, entre otros,
            recortes, montajes, subtítulos, incorporación de música, efectos
            visuales y adaptación a diferentes formatos digitales o impresos.
          </p>

          <p className="mt-3">
            La presente autorización se otorga de forma{" "}
            <strong>voluntaria</strong>,<strong> gratuita</strong>,{" "}
            <strong>sin limitación territorial</strong> y con una duración{" "}
            <strong>indefinida</strong>, salvo revocación expresa por escrito
            por parte de El Profesional.
          </p>

          <p className="mt-3">
            No obstante, la eventual revocación no afectará a aquellos
            materiales, publicaciones o campañas que ya hubieran sido
            difundidos, distribuidos o publicados con anterioridad a la fecha de
            recepción de dicha revocación.
          </p>

          <p className="mt-3">
            El Profesional reconoce y acepta que esta autorización no genera
            derecho a compensación económica, remuneración futura ni relación
            laboral adicional alguna derivada del uso autorizado de su imagen,
            voz o apariencia.
          </p>

          <p className="mt-3">
            <strong>CUADRANEX, S.L.</strong> se compromete a hacer un uso
            respetuoso de las imágenes y grabaciones, conforme a la legislación
            vigente en materia de protección de datos, derecho al honor,
            intimidad personal y propia imagen, absteniéndose de cualquier
            utilización que pudiera perjudicar la dignidad, reputación o imagen
            personal del autorizado.
          </p>
        </div>

        {/* FIRMA */}
        {firmaBase64 && (
          <div className="pdf-section no-page-break mt-6">
            <p className="mb-4">
              Y en prueba de conformidad, firman el presente contrato por
              duplicado y a un solo efecto:
            </p>

            <div className="flex justify-between mt-10">
              <div className="text-center">
                <p className="font-semibold mb-6">La Empresa</p>
                <div className="pdf-section flex justify-between items-center">
                  <img
                    src="/img/logo/selloCuadranex.png"
                    alt="Logo"
                    className="w-60 h-20"
                  />
                </div>
                <p>LinkServs</p>
              </div>

              <div className="text-center">
                <p className="font-semibold mb-2">El Profesional</p>

                <img
                  src={firmaUrl || firmaBase64}
                  alt="Firma del cliente"
                  style={{ width: "180px", height: "100px", margin: "0 auto" }}
                />

                <p className="mt-2 text-sm">Firma y aceptación</p>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  },
);

// Exporta el componente para su uso en otros módulos
export default ContratoServicios;
